/*
 * Starlight — a low-cost animated canvas that renders ~40 gently twinkling
 * points of light across a container. Used as an atmospheric backdrop for
 * the Dashboard hero, giving the app a "quiet night sky" feel without any
 * heavy image assets or third-party libraries.
 *
 * Design goals:
 *   – Unisex, nature-inspired, non-religious (a sky of stars — universal).
 *   – Very low opacity so it never fights foreground text.
 *   – Uses only the existing palette tokens (gold #D4AF37 & alabaster).
 *   – Respects prefers-reduced-motion — falls back to a static field.
 */
import React, { useEffect, useRef } from 'react';

export default function Starlight({
  count = 42,
  className = '',
  style = {},
  glowColor = 'rgba(212, 175, 55, 0.85)',    // gold
  starColor = 'rgba(253, 251, 247, 0.85)',   // alabaster
}) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.scale(dpr, dpr);
    };
    resize();

    const w = () => canvas.getBoundingClientRect().width;
    const h = () => canvas.getBoundingClientRect().height;

    // Distribute stars randomly, each with its own twinkle phase & speed
    const stars = Array.from({ length: count }, () => {
      const isEmber = Math.random() < 0.18; // ~1 in 6 gets a warm gold hue
      return {
        x: Math.random() * w(),
        y: Math.random() * h(),
        r: Math.random() * 1.4 + 0.3,
        base: Math.random() * 0.35 + 0.25,     // baseline opacity
        amp: Math.random() * 0.35 + 0.15,      // twinkle amplitude
        speed: Math.random() * 0.0009 + 0.0004,
        phase: Math.random() * Math.PI * 2,
        color: isEmber ? glowColor : starColor,
      };
    });

    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let rafId;
    let last = performance.now();

    const draw = (now) => {
      const dt = now - last;
      last = now;
      const cw = w();
      const ch = h();
      ctx.clearRect(0, 0, cw, ch);

      for (const s of stars) {
        if (!reduced) {
          s.phase += s.speed * dt;
        }
        const alpha = s.base + Math.sin(s.phase) * s.amp;
        ctx.globalAlpha = Math.max(0.05, Math.min(1, alpha));
        ctx.fillStyle = s.color;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fill();

        // Soft glow around larger stars (only during peak brightness)
        if (s.r > 1 && alpha > 0.55) {
          ctx.globalAlpha = Math.max(0, (alpha - 0.55) * 0.5);
          ctx.beginPath();
          ctx.arc(s.x, s.y, s.r * 3, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      ctx.globalAlpha = 1;

      if (!reduced) rafId = requestAnimationFrame(draw);
    };

    const onResize = () => {
      resize();
      // Reflow stars so they don't cluster after a window resize
      stars.forEach((s) => {
        s.x = Math.min(s.x, w());
        s.y = Math.min(s.y, h());
      });
    };
    window.addEventListener('resize', onResize);

    if (reduced) {
      // Draw one static frame
      draw(performance.now());
    } else {
      rafId = requestAnimationFrame(draw);
    }

    return () => {
      window.removeEventListener('resize', onResize);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [count, glowColor, starColor]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      className={`pointer-events-none absolute inset-0 ${className}`}
      style={{ width: '100%', height: '100%', ...style }}
      data-testid="dashboard-starlight"
    />
  );
}
